import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { okrService, keyResultService, departamentoService, okrAtividadeService, usuarioService } from '@/services/api';
import { OKR, KeyResult, Departamento, OKRStatus, OKRAtividade, Usuario } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus, History, User, PlusCircle, RefreshCw, TrendingUp, XCircle, MessageSquare, Edit3,
  FileText, CheckCircle, Send, Paperclip, Ban, List, LayoutDashboard, ChevronsUpDown, Check, Loader2,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';
import { OKRListView } from '@/components/okrs/OKRListView';
import { OKRBoardView, columns as kanbanColumns, loadHiddenColumns, HIDDEN_COLUMNS_STORAGE_KEY } from '@/components/okrs/OKRBoardView';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function getAtividadeIcon(tipo: OKRAtividade['tipo']) {
  switch (tipo) {
    case 'criacao': return PlusCircle;
    case 'atualizacao_status': return RefreshCw;
    case 'atualizacao_key_result': return TrendingUp;
    case 'cancelamento': return XCircle;
    case 'retorno_admin': return MessageSquare;
    case 'edicao': return Edit3;
    case 'solicitacao_conclusao': return Send;
    case 'aprovacao_conclusao': return CheckCircle;
    case 'rejeicao_conclusao': return XCircle;
    default: return FileText;
  }
}

function getAtividadeLabel(tipo: OKRAtividade['tipo']): string {
  switch (tipo) {
    case 'criacao': return 'Criação';
    case 'atualizacao_status': return 'Status';
    case 'atualizacao_key_result': return 'Key Result';
    case 'cancelamento': return 'Cancelamento';
    case 'retorno_admin': return 'Retorno Admin';
    case 'edicao': return 'Edição';
    case 'solicitacao_conclusao': return 'Solicitação';
    case 'aprovacao_conclusao': return 'Aprovado';
    case 'rejeicao_conclusao': return 'Rejeitado';
    default: return 'Atividade';
  }
}

function getAtividadeBadgeClass(tipo: OKRAtividade['tipo']): string {
  switch (tipo) {
    case 'criacao': return 'bg-green-100 text-green-800';
    case 'atualizacao_status': return 'bg-blue-100 text-blue-800';
    case 'atualizacao_key_result': return 'bg-purple-100 text-purple-800';
    case 'cancelamento': return 'bg-red-100 text-red-800';
    case 'retorno_admin': return 'bg-amber-100 text-amber-800';
    case 'edicao': return 'bg-gray-100 text-gray-800';
    case 'solicitacao_conclusao': return 'bg-blue-100 text-blue-800';
    case 'aprovacao_conclusao': return 'bg-green-100 text-green-800';
    case 'rejeicao_conclusao': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export default function OKRs() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') ?? 'list') as 'list' | 'board';

  // Evita que o clique que dá foco no campo desfaça o select-all do onFocus,
  // sem bloquear o mouseup dos cliques seguintes (ex: setas do input number).
  const progressoAtualJustFocused = useRef(false);
  const completionProgressJustFocused = useRef(false);

  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter states
  const [filterDepartamento, setFilterDepartamento] = useState<string>('all');
  const [filterPessoa, setFilterPessoa] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDataInicio, setFilterDataInicio] = useState<string>('');
  const [filterDataFim, setFilterDataFim] = useState<string>('');
  const [hiddenColumns, setHiddenColumns] = useState<OKRStatus[]>(() => loadHiddenColumns());
  const [allKRsExpanded, setAllKRsExpanded] = useState(false);

  useEffect(() => {
    localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const toggleColumnVisibility = (columnId: OKRStatus) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
    );
  };

  // OKR form dialog states
  const [isOKRDialogOpen, setIsOKRDialogOpen] = useState(false);
  // true quando a OKR foi aberta via deep-link de outra tela (perfil do usuário) — superadmin
  // só visualiza, não edita OKR de outra empresa só de estar auditando (28/09/2026).
  const [okrSomenteLeitura, setOkrSomenteLeitura] = useState(false);
  const [editingOKR, setEditingOKR] = useState<OKR | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteKRConfirmId, setDeleteKRConfirmId] = useState<string | null>(null);
  const [okrForm, setOkrForm] = useState({
    titulo: '',
    descricao: '',
    departamento_id: '',
    responsavel_id: '',
    status: 'a_fazer' as OKRStatus,
    data_inicio: '',
    data_fim: '',
  });
  const [okrFormInitial, setOkrFormInitial] = useState({ ...okrForm });
  const [okrFormErrors, setOkrFormErrors] = useState({ titulo: '', data_inicio: '', data_fim: '', departamento_id: '' });
  // Janela de anos aceitável para datas de OKR/Key Result (ano atual -1 a +10) — mantida em sincronia com backend/src/utils/dateValidation.ts
  const anoAtual = new Date().getFullYear();
  const dataMin = `${anoAtual - 1}-01-01`;
  const dataMax = `${anoAtual + 10}-12-31`;
  // min/max do <input type="date"> só bloqueiam a escolha no calendário, não o valor digitado direto — por isso
  // esta função reforça a checagem no onChange (comparação de string funciona pois o formato é sempre AAAA-MM-DD).
  const handleFilterDateChange = (value: string, setter: (v: string) => void) => {
    if (value && (value < dataMin || value > dataMax)) {
      toast.error(`Ano fora do intervalo permitido (${anoAtual - 1}–${anoAtual + 10})`);
      return;
    }
    setter(value);
  };
  const [showUnsavedOKR, setShowUnsavedOKR] = useState(false);
  const [depPopoverOpen, setDepPopoverOpen] = useState(false);
  const [depSearch, setDepSearch] = useState('');
  const [isNewDepDialogOpen, setIsNewDepDialogOpen] = useState(false);
  const [newDepNome, setNewDepNome] = useState('');
  const [savingNewDep, setSavingNewDep] = useState(false);

  // KR form dialog states
  const [isKRDialogOpen, setIsKRDialogOpen] = useState(false);
  const [editingKR, setEditingKR] = useState<KeyResult | null>(null);
  const [currentOKRId, setCurrentOKRId] = useState<string | null>(null);
  const [krForm, setKrForm] = useState({
    descricao: '',
    responsavel_id: '',
    progresso_atual: 0,
    progresso_meta: 100,
    prazo: '',
    anexo_obrigatorio: false,
    aprovar_alteracoes: false,
  });
  const [krFormInitial, setKrFormInitial] = useState({ ...krForm });
  const [krFormErrors, setKrFormErrors] = useState({ descricao: '' });
  const [showUnsavedKR, setShowUnsavedKR] = useState(false);
  const [krAnexoFile, setKrAnexoFile] = useState<File | null>(null);

  // Completion dialog states
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completionKR, setCompletionKR] = useState<KeyResult | null>(null);
  const [completionProgress, setCompletionProgress] = useState(0);
  const [completionProgressInitial, setCompletionProgressInitial] = useState(0);
  const [completionFile, setCompletionFile] = useState<File | null>(null);
  const [completionObservacao, setCompletionObservacao] = useState('');
  const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);
  const [showUnsavedCompletion, setShowUnsavedCompletion] = useState(false);

  // Cancel dialog states (board view)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [okrToCancel, setOkrToCancel] = useState<OKR | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  // Historico dialog states
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [historicoOkr, setHistoricoOkr] = useState<OKR | null>(null);
  const [atividades, setAtividades] = useState<OKRAtividade[]>([]);
  const [loadingAtividades, setLoadingAtividades] = useState(false);

  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';
  // Empresa filtrada vem do seletor global do header: pro superadmin é o
  // `empresaFiltroId` (pode ser "todas as empresas"); pra admin/comum é a empresa vinculada.
  const selectedEmpresa = (isSuperAdmin ? empresaFiltroId : empresaAtiva?.id) ?? 'all';

  const hasOkrChanges = useCallback(() => JSON.stringify(okrForm) !== JSON.stringify(okrFormInitial), [okrForm, okrFormInitial]);
  const hasKrChanges = useCallback(() => JSON.stringify(krForm) !== JSON.stringify(krFormInitial), [krForm, krFormInitial]);
  const hasCompletionChanges = useCallback(
    () => completionProgress !== completionProgressInitial || completionFile !== null,
    [completionProgress, completionProgressInitial, completionFile]
  );

  const loadData = useCallback(async () => {
    if (!empresaAtiva?.id && user?.tipo !== 'superadmin') return;
    // OKRs exige uma empresa específica — "Todas as empresas" só existe em Dashboard e
    // Usuários. Sem empresa escolhida, nem tenta carregar (evita erro de dados sem escopo).
    if (isSuperAdmin && selectedEmpresa === 'all') {
      setOkrs([]);
      setDepartamentos([]);
      setUsuarios([]);
      setIsLoading(false);
      return;
    }
    try {
      const empresaId = isSuperAdmin ? selectedEmpresa : (empresaAtiva?.id || '');

      const [okrsRes, krsRes, depsRes, usersRes] = await Promise.all([
        okrService.getByEmpresa(empresaId),
        keyResultService.getAll(),
        departamentoService.getByEmpresa(empresaId),
        usuarioService.getByEmpresa(empresaId),
      ]);
      if (okrsRes.success) setOkrs(okrsRes.data);
      if (krsRes.success) setKeyResults(krsRes.data);
      if (depsRes.success) setDepartamentos(depsRes.data);
      if (usersRes.success) setUsuarios(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Não foi possível carregar os OKRs. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [user, empresaAtiva, selectedEmpresa, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && selectedEmpresa !== 'all') setFilterDepartamento('all');
    setFilterPessoa('all');
  }, [selectedEmpresa, isSuperAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered OKRs — filterStatus is ignored in board view (board already separates by columns)
  const filteredDepartamentos = isSuperAdmin && selectedEmpresa !== 'all'
    ? departamentos.filter((d) => d.empresa_id === selectedEmpresa)
    : departamentos;

  const filteredOkrs = okrs.filter((okr) => {
    if (isSuperAdmin && selectedEmpresa !== 'all' && okr.empresa_id !== selectedEmpresa) return false;
    if (filterDepartamento !== 'all' && okr.departamento_id !== filterDepartamento) return false;
    if (filterPessoa !== 'all' && okr.responsavel_id !== filterPessoa) return false;
    if (view === 'list' && filterStatus !== 'all' && okr.status !== filterStatus) return false;
    if (filterDataInicio && okr.data_inicio < filterDataInicio) return false;
    if (filterDataFim && okr.data_fim > filterDataFim) return false;
    return true;
  });

  const hasActiveFilters =
    (isSuperAdmin && selectedEmpresa !== 'all') ||
    filterDepartamento !== 'all' ||
    filterPessoa !== 'all' ||
    (view === 'list' && filterStatus !== 'all') ||
    !!filterDataInicio ||
    !!filterDataFim;

  const clearFilters = () => {
    setFilterDepartamento('all');
    setFilterPessoa('all');
    setFilterStatus('all');
    setFilterDataInicio('');
    setFilterDataFim('');
  };

  // OKR handlers
  const resetOKRForm = () => {
    const initial = { titulo: '', descricao: '', departamento_id: '', responsavel_id: '', status: 'a_fazer' as OKRStatus, data_inicio: '', data_fim: '' };
    setOkrForm(initial);
    setOkrFormInitial(initial);
    setEditingOKR(null);
    setOkrFormErrors({ titulo: '', data_inicio: '', data_fim: '', departamento_id: '' });
    setOkrSomenteLeitura(false);
  };

  const openEditOKR = (okr: OKR, somenteLeitura = false) => {
    setEditingOKR(okr);
    const formData = { titulo: okr.titulo, descricao: okr.descricao, departamento_id: okr.departamento_id, responsavel_id: okr.responsavel_id || '', status: okr.status, data_inicio: okr.data_inicio, data_fim: okr.data_fim };
    setOkrForm(formData);
    setOkrFormInitial(formData);
    setOkrSomenteLeitura(somenteLeitura);
    setIsOKRDialogOpen(true);
  };

  // Deep-link vindo de outra tela (ex: card de OKR no perfil do usuário, ?okr=<id>): abre o
  // dialog dessa OKR assim que a lista carregar, só visualização — superadmin auditando outra
  // pessoa não deve poder editar OKR de empresa alheia (decisão da Samantha, 28/09/2026).
  useEffect(() => {
    const okrParam = searchParams.get('okr');
    if (!okrParam || okrs.length === 0) return;
    const alvo = okrs.find((o) => o.id === okrParam);
    if (!alvo) return;
    openEditOKR(alvo, true);
    const next = new URLSearchParams(searchParams);
    next.delete('okr');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [okrs]);

  const handleCloseOKRDialog = useCallback(() => {
    if (hasOkrChanges()) {
      setShowUnsavedOKR(true);
    } else {
      setIsOKRDialogOpen(false);
      resetOKRForm();
    }
  }, [hasOkrChanges]);

  const handleSaveOKR = async () => {
    const empresaIdParaCriar = isSuperAdmin
      ? (selectedEmpresa !== 'all' ? selectedEmpresa : '')
      : (empresaAtiva?.id || '');
    if (!editingOKR && !empresaIdParaCriar) {
      toast.error(isSuperAdmin
        ? 'Selecione uma empresa específica no filtro do topo antes de criar a OKR'
        : 'Empresa não identificada');
      return;
    }
    const errors = { titulo: '', data_inicio: '', data_fim: '', departamento_id: '' };
    let hasErrors = false;
    if (!okrForm.titulo?.trim()) { errors.titulo = 'Título é obrigatório'; hasErrors = true; }
    else if (okrForm.titulo.trim().length < 10) { errors.titulo = 'Título deve ter pelo menos 10 caracteres'; hasErrors = true; }
    if (!okrForm.departamento_id?.trim()) { errors.departamento_id = 'Departamento é obrigatório'; hasErrors = true; }
    if (!okrForm.data_inicio?.trim()) { errors.data_inicio = 'Data de início é obrigatória'; hasErrors = true; }
    if (!okrForm.data_fim?.trim()) { errors.data_fim = 'Data de fim é obrigatória'; hasErrors = true; }
    if (okrForm.data_inicio && okrForm.data_fim && new Date(okrForm.data_fim) < new Date(okrForm.data_inicio)) {
      errors.data_fim = 'Data de fim deve ser posterior à data de início';
      hasErrors = true;
    }
    setOkrFormErrors(errors);
    if (hasErrors) { toast.error(Object.values(errors).filter(Boolean).join(' ')); return; }
    try {
      if (editingOKR) {
        const result = await okrService.update(editingOKR.id, okrForm);
        if (result.success) { toast.success('OKR atualizada'); loadData(); }
        else { toast.error(result.error || 'Erro ao atualizar OKR. Tente novamente.'); return; }
      } else {
        const result = await okrService.create({ ...okrForm, empresa_id: empresaIdParaCriar });
        if (result.success) { toast.success('OKR criada'); loadData(); }
        else { toast.error(result.error || 'Erro ao criar OKR. Tente novamente.'); return; }
      }
      setIsOKRDialogOpen(false);
      resetOKRForm();
    } catch {
      toast.error('Não foi possível salvar a OKR. Verifique sua conexão e tente novamente.');
    }
  };

  const handleDeleteOKR = async () => {
    if (!deleteConfirmId) return;
    const result = await okrService.delete(deleteConfirmId);
    if (result.success) { toast.success('OKR excluída'); loadData(); }
    setDeleteConfirmId(null);
  };

  const handleMoveOKR = async (okrId: string, newStatus: OKRStatus) => {
    const result = await okrService.updateStatus(okrId, newStatus, user?.nome);
    if (result.success) {
      setOkrs((prev) => prev.map((o) => (o.id === okrId ? { ...o, status: newStatus } : o)));
      toast.success('Status atualizado');
    } else {
      toast.error(result.error || 'Erro ao atualizar status da OKR. Tente novamente.');
    }
  };

  const handleUpdateKR = async (kr: KeyResult, progresso: number, anexoFile?: File | null) => {
    const isComum = user?.tipo === 'comum';
    const exigeSolicitacao = isComum && (Boolean(kr.aprovar_alteracoes) || progresso >= kr.progresso_meta);
    if (exigeSolicitacao) {
      let anexoUrl: string | undefined;
      let anexoNome: string | undefined;
      if (anexoFile) {
        const uploadRes = await keyResultService.uploadAnexo(kr.id, anexoFile);
        if (!uploadRes.success) { toast.error(uploadRes.error || 'Erro ao enviar o anexo. Tente novamente.'); return; }
        anexoUrl = uploadRes.data.url;
        anexoNome = uploadRes.data.nome;
      }
      const result = await keyResultService.requestCompletion(kr.id, progresso, user?.nome || '', anexoUrl, anexoNome);
      if (result.success) { toast.success('Solicitação enviada para aprovação'); await loadData(); }
      else toast.error(result.error || 'Erro ao enviar solicitação de conclusão. Tente novamente.');
      return;
    }
    const exigeAnexo = kr.anexo_obrigatorio && progresso >= kr.progresso_meta && !kr.anexo_url;
    if (exigeAnexo && !anexoFile) {
      toast.error('Este Key Result exige um anexo para ser concluído. Selecione um arquivo antes de salvar.');
      return;
    }
    let anexoUrl: string | undefined;
    let anexoNome: string | undefined;
    if (anexoFile) {
      const uploadRes = await keyResultService.uploadAnexo(kr.id, anexoFile);
      if (!uploadRes.success) { toast.error(uploadRes.error || 'Erro ao enviar o anexo. Tente novamente.'); return; }
      anexoUrl = uploadRes.data.url;
      anexoNome = uploadRes.data.nome;
    }
    const result = await keyResultService.updateProgress(kr.id, progresso, undefined, anexoUrl, anexoNome);
    if (result.success && result.data) {
      setKeyResults((prev) => prev.map((item) => (item.id === kr.id ? result.data! : item)));
      await loadData();
      toast.success('Progresso atualizado');
    } else {
      toast.error(result.error || 'Erro ao atualizar o progresso do Key Result. Tente novamente.');
    }
  };

  // KR handlers
  const resetKRForm = () => {
    const initial = { descricao: '', responsavel_id: '', progresso_atual: 0, progresso_meta: 100, prazo: '', anexo_obrigatorio: false, aprovar_alteracoes: false };
    setKrForm(initial);
    setKrFormInitial(initial);
    setEditingKR(null);
    setCurrentOKRId(null);
    setKrFormErrors({ descricao: '' });
    setKrAnexoFile(null);
  };

  const openAddKR = (okrId: string) => {
    setCurrentOKRId(okrId);
    const initial = { descricao: '', responsavel_id: '', progresso_atual: 0, progresso_meta: 100, prazo: '', anexo_obrigatorio: false, aprovar_alteracoes: false };
    setKrForm(initial);
    setKrFormInitial(initial);
    setKrFormErrors({ descricao: '' });
    setKrAnexoFile(null);
    setIsKRDialogOpen(true);
  };

  const openEditKR = async (kr: KeyResult, okrId: string) => {
    setEditingKR(kr);
    setCurrentOKRId(okrId);
    try {
      const result = await keyResultService.getById(kr.id);
      const source = result.success && result.data ? result.data : kr;
      setEditingKR(source);
      const formData = {
        descricao: source.descricao,
        responsavel_id: source.responsavel_id || '',
        progresso_atual: source.progresso_atual,
        progresso_meta: source.progresso_meta,
        prazo: source.prazo || '',
        anexo_obrigatorio: Boolean(source.anexo_obrigatorio ?? false),
        aprovar_alteracoes: Boolean(source.aprovar_alteracoes ?? false),
      };
      setKrForm(formData);
      setKrFormInitial(formData);
    } catch {
      const formData = {
        descricao: kr.descricao,
        responsavel_id: kr.responsavel_id || '',
        progresso_atual: kr.progresso_atual,
        progresso_meta: kr.progresso_meta,
        prazo: kr.prazo || '',
        anexo_obrigatorio: Boolean(kr.anexo_obrigatorio ?? false),
        aprovar_alteracoes: Boolean(kr.aprovar_alteracoes ?? false),
      };
      setKrForm(formData);
      setKrFormInitial(formData);
    }
    setKrFormErrors({ descricao: '' });
    setKrAnexoFile(null);
    setIsKRDialogOpen(true);
  };

  const handleCloseKRDialog = useCallback(() => {
    if (hasKrChanges()) setShowUnsavedKR(true);
    else { setIsKRDialogOpen(false); resetKRForm(); }
  }, [hasKrChanges]);

  const handleSaveKR = async () => {
    if (!currentOKRId) return;
    if (!krForm.descricao?.trim()) {
      setKrFormErrors({ descricao: 'Descrição é obrigatória' });
      toast.error('Descrição é obrigatória');
      return;
    }
    if (krForm.progresso_meta < krForm.progresso_atual) {
      toast.error('A meta não pode ser menor que o progresso atual');
      return;
    }
    try {
      if (editingKR) {
        const concluindo = krForm.progresso_atual >= krForm.progresso_meta;
        const exigeAnexo = krForm.anexo_obrigatorio && concluindo && !editingKR.anexo_url;
        if (exigeAnexo && !krAnexoFile) {
          toast.error('Este Key Result exige um anexo para ser concluído. Selecione um arquivo antes de salvar.');
          return;
        }
        let anexoUrl: string | undefined;
        let anexoNome: string | undefined;
        if (krAnexoFile) {
          const uploadRes = await keyResultService.uploadAnexo(editingKR.id, krAnexoFile);
          if (!uploadRes.success) { toast.error(uploadRes.error || 'Erro ao enviar o anexo. Tente novamente.'); return; }
          anexoUrl = uploadRes.data.url;
          anexoNome = uploadRes.data.nome;
        }
        const updateData = {
          ...krForm,
          prazo: krForm.prazo?.trim() ? krForm.prazo : null,
          ...(anexoUrl && { anexo_url: anexoUrl, anexo_nome: anexoNome }),
        };
        const result = await keyResultService.update(editingKR.id, updateData);
        if (result.success) { toast.success('Key Result atualizado'); loadData(); }
        else { toast.error(result.error || 'Erro ao atualizar Key Result. Tente novamente.'); return; }
      } else {
        const krsForOkr = keyResults.filter((kr) => kr.okr_id === currentOKRId);
        const maxOrdem = krsForOkr.length > 0 ? Math.max(...krsForOkr.map((kr) => kr.ordem)) : -1;
        const result = await keyResultService.create({
          descricao: krForm.descricao,
          responsavel_id: krForm.responsavel_id || undefined,
          progresso_atual: krForm.progresso_atual,
          progresso_meta: krForm.progresso_meta,
          prazo: krForm.prazo || undefined,
          okr_id: currentOKRId,
          ordem: maxOrdem + 1,
          anexo_obrigatorio: krForm.anexo_obrigatorio,
          aprovar_alteracoes: krForm.aprovar_alteracoes,
        });
        if (result.success) { toast.success('Key Result criado'); loadData(); }
        else { toast.error(result.error || 'Erro ao criar Key Result. Tente novamente.'); return; }
      }
      setIsKRDialogOpen(false);
      resetKRForm();
    } catch {
      toast.error('Não foi possível salvar o Key Result. Verifique sua conexão e tente novamente.');
    }
  };

  const handleDeleteKR = async () => {
    if (!deleteKRConfirmId) return;
    const result = await keyResultService.delete(deleteKRConfirmId);
    if (result.success) { toast.success('Key Result excluído'); loadData(); }
    else { toast.error(result.error || 'Erro ao excluir Key Result. Tente novamente.'); }
    setDeleteKRConfirmId(null);
  };

  const handleReorderKR = async (okrId: string, krId: string, direction: 'up' | 'down') => {
    const result = await keyResultService.reorder(okrId, krId, direction);
    if (result.success) loadData();
  };

  // Completion handlers
  const openCompletionDialog = (kr: KeyResult) => {
    setCompletionKR(kr);
    setCompletionProgress(kr.progresso_meta);
    setCompletionProgressInitial(kr.progresso_meta);
    setCompletionFile(null);
    setCompletionObservacao('');
    setCompletionDialogOpen(true);
  };

  const handleCloseCompletionDialog = useCallback(() => {
    if (hasCompletionChanges()) setShowUnsavedCompletion(true);
    else { setCompletionDialogOpen(false); setCompletionKR(null); setCompletionFile(null); setCompletionObservacao(''); }
  }, [hasCompletionChanges]);

  const handleRequestCompletion = async () => {
    if (!completionKR || !user) return;
    if (completionKR.anexo_obrigatorio && !completionFile) {
      toast.error('É obrigatório anexar um arquivo para este Key Result');
      return;
    }
    try {
      setIsSubmittingCompletion(true);
      let anexoUrl: string | undefined;
      let anexoNome: string | undefined;
      if (completionFile) {
        const uploadRes = await keyResultService.uploadAnexo(completionKR.id, completionFile);
        if (!uploadRes.success) { toast.error(uploadRes.error || 'Erro ao enviar o anexo. Tente novamente.'); return; }
        anexoUrl = uploadRes.data.url;
        anexoNome = uploadRes.data.nome;
      }
      const result = await keyResultService.requestCompletion(completionKR.id, completionProgress, user.nome, anexoUrl, anexoNome, completionObservacao || undefined);
      if (result.success) {
        toast.success('Solicitação enviada para aprovação');
        setCompletionDialogOpen(false);
        setCompletionKR(null);
        setCompletionFile(null);
        setCompletionObservacao('');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao enviar solicitação de conclusão. Tente novamente.');
      }
    } finally {
      setIsSubmittingCompletion(false);
    }
  };

  // Cancel handlers (board view)
  const handleOpenCancelDialog = (okr: OKR) => {
    setOkrToCancel(okr);
    setMotivoCancelamento('');
    setCancelDialogOpen(true);
  };

  const handleCancelarOKR = async () => {
    if (!okrToCancel || !motivoCancelamento.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    const result = await okrService.cancelar(okrToCancel.id, motivoCancelamento.trim(), user?.nome || 'Usuário');
    if (result.success && result.data) {
      setOkrs((prev) => prev.map((o) => (o.id === okrToCancel.id ? result.data! : o)));
      toast.success('OKR cancelada com sucesso');
      setCancelDialogOpen(false);
      setOkrToCancel(null);
      setMotivoCancelamento('');
    } else {
      toast.error(result.error || 'Erro ao cancelar OKR. Tente novamente.');
    }
  };

  // Historico handlers
  const handleOpenHistorico = async (okr: OKR) => {
    setHistoricoOkr(okr);
    setHistoricoDialogOpen(true);
    setLoadingAtividades(true);
    const result = await okrAtividadeService.getByOkr(okr.id);
    if (result.success) setAtividades(result.data);
    setLoadingAtividades(false);
  };

  // Departamento handlers
  const handleCreateDepartamento = async () => {
    if (!newDepNome.trim()) return;
    setSavingNewDep(true);
    try {
      const empresaId = empresaAtiva?.id || '';
      const result = await departamentoService.create({ nome: newDepNome.trim(), empresa_id: empresaId });
      if (result.success) {
        const depsRes = user?.tipo === 'superadmin' ? await departamentoService.getAll() : await departamentoService.getByEmpresa(empresaId);
        if (depsRes.success) {
          setDepartamentos(depsRes.data);
          setOkrForm((f) => ({ ...f, departamento_id: result.data.id }));
        }
        setNewDepNome('');
        setIsNewDepDialogOpen(false);
        toast.success('Departamento criado');
      } else {
        toast.error(result.error || 'Erro ao criar departamento. Tente novamente.');
      }
    } catch {
      toast.error('Não foi possível criar o departamento. Verifique sua conexão e tente novamente.');
    } finally {
      setSavingNewDep(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-muted rounded-lg" />)}
          </div>
        </div>
        <GuiaModuloFlutuante imagemSrc="/guias-modulos/okrs.png" titulo="OKRs" />
      </AppLayout>
    );
  }

  if (isSuperAdmin && selectedEmpresa === 'all') {
    return (
      <AppLayout>
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
          <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            OKRs é por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
          </p>
        </div>
        <GuiaModuloFlutuante imagemSrc="/guias-modulos/okrs.png" titulo="OKRs" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">

        {/* Header */}
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">OKRs</h1>
            <p className="text-muted-foreground">
              {view === 'list' ? 'Gerencie seus objetivos e resultados-chave' : 'Visualize e gerencie suas OKRs por status'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center border rounded-lg p-1 gap-0.5">
              <Button
                variant={view === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setSearchParams({ view: 'list' })}
                title="Visualização em lista"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={view === 'board' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setSearchParams({ view: 'board' })}
                title="Visualização em quadro"
              >
                <LayoutDashboard className="h-4 w-4" />
              </Button>
            </div>
            {isAdmin && (
              <Button onClick={() => { resetOKRForm(); setIsOKRDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nova OKR
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-end gap-3">
            <div className="w-full md:flex-1 md:min-w-[150px] md:max-w-[200px]">
              <Label className="text-xs text-muted-foreground">Departamento</Label>
              <Select value={filterDepartamento} onValueChange={setFilterDepartamento}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filteredDepartamentos.map((dep) => <SelectItem key={dep.id} value={dep.id}>{dep.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:flex-1 md:min-w-[150px] md:max-w-[200px]">
              <Label className="text-xs text-muted-foreground">Pessoa</Label>
              <Select value={filterPessoa} onValueChange={setFilterPessoa}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {view === 'list' && (
              <div className="w-full md:flex-1 md:min-w-[150px] md:max-w-[200px]">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="a_fazer">A Fazer</SelectItem>
                    <SelectItem value="fazendo">Fazendo</SelectItem>
                    <SelectItem value="pausado">Pausado</SelectItem>
                    <SelectItem value="feito">Feito</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-3 md:contents">
              <div className="min-w-0 flex-1 md:min-w-[140px] md:max-w-[180px]">
                <Label className="text-xs text-muted-foreground">Data Início</Label>
                <Input type="date" min={dataMin} max={dataMax} value={filterDataInicio} onChange={(e) => handleFilterDateChange(e.target.value, setFilterDataInicio)} className="h-9 w-full min-w-0" />
              </div>
              <div className="min-w-0 flex-1 md:min-w-[140px] md:max-w-[180px]">
                <Label className="text-xs text-muted-foreground">Data Fim</Label>
                <Input type="date" min={dataMin} max={dataMax} value={filterDataFim} onChange={(e) => handleFilterDateChange(e.target.value, setFilterDataFim)} className="h-9 w-full min-w-0" />
              </div>
            </div>
            <div className="w-full md:w-auto md:ml-auto flex gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={() => setAllKRsExpanded((prev) => !prev)}
                title={allKRsExpanded ? 'Ocultar todos os Key Results' : 'Ver todos os Key Results'}
              >
                {allKRsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
              {view === 'board' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9" title="Mostrar/ocultar colunas do Kanban">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Colunas visíveis</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {[...kanbanColumns, { id: 'cancelado' as OKRStatus, title: 'Cancelado' }].map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={!hiddenColumns.includes(column.id)}
                        onCheckedChange={() => toggleColumnVisibility(column.id)}
                      >
                        {column.title}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 w-full md:w-auto">
                <XCircle className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* View content */}
        {view === 'list' ? (
          <OKRListView
            okrs={filteredOkrs}
            keyResults={keyResults}
            departamentos={departamentos}
            isAdmin={isAdmin}
            onEditOKR={openEditOKR}
            onDeleteOKR={(id) => setDeleteConfirmId(id)}
            onOpenHistorico={handleOpenHistorico}
            onAddKR={openAddKR}
            onEditKR={openEditKR}
            onDeleteKR={(krId) => setDeleteKRConfirmId(krId)}
            onReorderKR={handleReorderKR}
            onRequestCompletion={openCompletionDialog}
            hasActiveFilters={hasActiveFilters}
            allExpanded={allKRsExpanded}
          />
        ) : (
          <OKRBoardView
            okrs={filteredOkrs}
            keyResults={keyResults}
            departamentos={departamentos}
            isAdmin={isAdmin}
            onMoveOKR={handleMoveOKR}
            onUpdateKR={handleUpdateKR}
            onOpenCancelDialog={handleOpenCancelDialog}
            onOpenHistorico={handleOpenHistorico}
            onEditOKR={openEditOKR}
            onAddKR={openAddKR}
            onDeleteOKR={(id) => setDeleteConfirmId(id)}
            onEditKR={openEditKR}
            onDeleteKR={(krId) => setDeleteKRConfirmId(krId)}
            onReorderKR={handleReorderKR}
            hiddenColumns={hiddenColumns}
            allKRsExpanded={allKRsExpanded}
          />
        )}
      </div>

      {/* ── OKR Dialog ── */}
      <Dialog open={isOKRDialogOpen} onOpenChange={(open) => { if (!open) handleCloseOKRDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{okrSomenteLeitura ? 'Visualizar OKR' : editingOKR ? 'Editar OKR' : 'Nova OKR'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                value={okrForm.titulo}
                onChange={(e) => { setOkrForm({ ...okrForm, titulo: e.target.value }); if (okrFormErrors.titulo) setOkrFormErrors({ ...okrFormErrors, titulo: '' }); }}
                placeholder="Ex: Aumentar receita em 20%"
                className={cn(okrFormErrors.titulo && 'border-red-500')}
                disabled={okrSomenteLeitura}
              />
              {okrFormErrors.titulo && <p className="text-sm text-red-500">{okrFormErrors.titulo}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea id="descricao" value={okrForm.descricao} onChange={(e) => setOkrForm({ ...okrForm, descricao: e.target.value })} placeholder="Descreva o objetivo..." disabled={okrSomenteLeitura} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="departamento">Departamento *</Label>
                <Popover open={depPopoverOpen} onOpenChange={setDepPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={okrSomenteLeitura}
                      className={cn(
                        'flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                        okrFormErrors.departamento_id ? 'border-red-500' : 'border-input'
                      )}
                    >
                      <span className={okrForm.departamento_id ? '' : 'text-muted-foreground'}>
                        {okrForm.departamento_id ? departamentos.find((d) => d.id === okrForm.departamento_id)?.nome : 'Selecione'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Buscar departamento..." value={depSearch} onValueChange={setDepSearch} />
                      <CommandList>
                        <CommandGroup>
                          {departamentos
                            .filter((dep) => dep.nome.toLowerCase().includes(depSearch.toLowerCase()))
                            .map((dep) => (
                              <CommandItem
                                key={dep.id}
                                value={dep.nome}
                                onSelect={() => {
                                  setOkrForm({ ...okrForm, departamento_id: dep.id });
                                  if (okrFormErrors.departamento_id) setOkrFormErrors({ ...okrFormErrors, departamento_id: '' });
                                  setDepPopoverOpen(false);
                                  setDepSearch('');
                                }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', okrForm.departamento_id === dep.id ? 'opacity-100' : 'opacity-0')} />
                                {dep.nome}
                              </CommandItem>
                            ))}
                          {departamentos.filter((dep) => dep.nome.toLowerCase().includes(depSearch.toLowerCase())).length === 0 && (
                            <div className="py-4 text-center text-sm text-muted-foreground">Nenhum departamento encontrado.</div>
                          )}
                        </CommandGroup>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem onSelect={() => { setDepPopoverOpen(false); setDepSearch(''); setIsNewDepDialogOpen(true); }} className="text-primary">
                            <Plus className="mr-2 h-4 w-4" />
                            Novo departamento
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {okrFormErrors.departamento_id && <p className="text-sm text-red-500">{okrFormErrors.departamento_id}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={okrForm.status}
                  onValueChange={(v) => {
                    if (v === 'cancelado') {
                      // Cancelamento exige motivo — abre o fluxo dedicado em vez de setar o status direto
                      if (editingOKR) {
                        setIsOKRDialogOpen(false);
                        handleOpenCancelDialog(editingOKR);
                      }
                      return;
                    }
                    setOkrForm({ ...okrForm, status: v as OKRStatus });
                  }}
                  disabled={okrSomenteLeitura}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a_fazer">A Fazer</SelectItem>
                    <SelectItem value="fazendo">Fazendo</SelectItem>
                    <SelectItem value="pausado">Pausado</SelectItem>
                    <SelectItem value="feito">Feito</SelectItem>
                    {editingOKR && <SelectItem value="cancelado">Cancelado</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="responsavel">Responsável</Label>
              <Select value={okrForm.responsavel_id || 'none'} onValueChange={(v) => setOkrForm({ ...okrForm, responsavel_id: v === 'none' ? '' : v })} disabled={okrSomenteLeitura}>
                <SelectTrigger id="responsavel"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="min-w-0 space-y-2">
                <Label htmlFor="data_inicio">Data Início *</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  min={dataMin}
                  max={dataMax}
                  value={okrForm.data_inicio}
                  onChange={(e) => { setOkrForm({ ...okrForm, data_inicio: e.target.value }); if (okrFormErrors.data_inicio) setOkrFormErrors({ ...okrFormErrors, data_inicio: '' }); }}
                  className={cn('w-full min-w-0', okrFormErrors.data_inicio && 'border-red-500')}
                  disabled={okrSomenteLeitura}
                />
                {okrFormErrors.data_inicio && <p className="text-sm text-red-500">{okrFormErrors.data_inicio}</p>}
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="data_fim">Data Fim *</Label>
                <Input
                  id="data_fim"
                  type="date"
                  min={dataMin}
                  max={dataMax}
                  value={okrForm.data_fim}
                  onChange={(e) => { setOkrForm({ ...okrForm, data_fim: e.target.value }); if (okrFormErrors.data_fim) setOkrFormErrors({ ...okrFormErrors, data_fim: '' }); }}
                  className={cn('w-full min-w-0', okrFormErrors.data_fim && 'border-red-500')}
                  disabled={okrSomenteLeitura}
                />
                {okrFormErrors.data_fim && <p className="text-sm text-red-500">{okrFormErrors.data_fim}</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseOKRDialog}>{okrSomenteLeitura ? 'Fechar' : 'Cancelar'}</Button>
            {!okrSomenteLeitura && (
              <Button onClick={handleSaveOKR}>{editingOKR ? 'Salvar' : 'Criar'}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedOKR}
        onOpenChange={setShowUnsavedOKR}
        onConfirm={() => { setShowUnsavedOKR(false); setIsOKRDialogOpen(false); resetOKRForm(); }}
        onCancel={() => setShowUnsavedOKR(false)}
      />

      {/* ── KR Dialog ── */}
      <Dialog open={isKRDialogOpen} onOpenChange={(open) => { if (!open) handleCloseKRDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingKR ? 'Editar Key Result' : 'Novo Key Result'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="kr_descricao">Descrição *</Label>
              <Textarea
                id="kr_descricao"
                value={krForm.descricao}
                onChange={(e) => { setKrForm({ ...krForm, descricao: e.target.value }); if (krFormErrors.descricao) setKrFormErrors({ descricao: '' }); }}
                placeholder="Ex: Aumentar NPS de 50 para 70"
                className={cn(krFormErrors.descricao && 'border-red-500')}
              />
              {krFormErrors.descricao && <p className="text-sm text-red-500">{krFormErrors.descricao}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="kr_responsavel">Responsável</Label>
              <Select value={krForm.responsavel_id || 'none'} onValueChange={(v) => setKrForm({ ...krForm, responsavel_id: v === 'none' ? '' : v })}>
                <SelectTrigger id="kr_responsavel"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="progresso_atual">Progresso Atual</Label>
                <Input
                  id="progresso_atual"
                  type="number"
                  min={0}
                  max={100}
                  value={krForm.progresso_atual}
                  onChange={(e) => setKrForm({ ...krForm, progresso_atual: Math.min(100, Math.max(0, Number(e.target.value))) })}
                  onFocus={(e) => { progressoAtualJustFocused.current = true; e.target.select(); }}
                  onMouseUp={(e) => { if (progressoAtualJustFocused.current) { e.preventDefault(); progressoAtualJustFocused.current = false; } }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="progresso_meta">Meta</Label>
                <Input
                  id="progresso_meta"
                  type="number"
                  min={1}
                  value={krForm.progresso_meta}
                  onChange={(e) => setKrForm({ ...krForm, progresso_meta: Math.max(1, Number(e.target.value)) })}
                  onFocus={(e) => e.target.select()}
                  onMouseUp={(e) => e.preventDefault()}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kr_prazo">Prazo (opcional)</Label>
              <Input id="kr_prazo" type="date" min={dataMin} max={dataMax} value={krForm.prazo} onChange={(e) => setKrForm({ ...krForm, prazo: e.target.value })} />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="anexo_obrigatorio" checked={krForm.anexo_obrigatorio} onCheckedChange={(checked) => setKrForm({ ...krForm, anexo_obrigatorio: checked === true })} />
              <Label htmlFor="anexo_obrigatorio" className="text-sm font-normal cursor-pointer">
                Exigir anexo para conclusão (PDF, CSV, Imagem)
              </Label>
            </div>
            {editingKR && krForm.anexo_obrigatorio && krForm.progresso_atual >= krForm.progresso_meta && !editingKR.anexo_url && (
              <div className="space-y-2">
                <Label htmlFor="kr_anexo_file">Anexo (obrigatório para concluir)</Label>
                <div className="flex items-center gap-2">
                  <Input id="kr_anexo_file" type="file" accept=".pdf,.csv,.png,.jpg,.jpeg,.gif,.webp" onChange={(e) => setKrAnexoFile(e.target.files?.[0] || null)} className="flex-1" />
                  {krAnexoFile && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setKrAnexoFile(null)}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {krAnexoFile && <p className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="h-3 w-3" />{krAnexoFile.name}</p>}
              </div>
            )}
            {isAdmin ? (
              <div className="flex items-center space-x-2">
                <Checkbox id="aprovar_alteracoes" checked={krForm.aprovar_alteracoes} onCheckedChange={(checked) => setKrForm({ ...krForm, aprovar_alteracoes: checked === true })} />
                <Label htmlFor="aprovar_alteracoes" className="text-sm font-normal cursor-pointer">
                  Exigir aprovação para cada alteração de progresso
                </Label>
              </div>
            ) : (
              <div className="flex items-center space-x-2 p-2 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Badge variant={krForm.aprovar_alteracoes ? 'default' : 'outline'} className={krForm.aprovar_alteracoes ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : ''}>
                    {krForm.aprovar_alteracoes ? 'Ativo' : 'Inativo'}
                  </Badge>
                  <Label className="text-sm font-normal">Exigir aprovação para alterações de progresso</Label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseKRDialog}>Cancelar</Button>
            <Button onClick={handleSaveKR}>{editingKR ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedKR}
        onOpenChange={setShowUnsavedKR}
        onConfirm={() => { setShowUnsavedKR(false); setIsKRDialogOpen(false); resetKRForm(); }}
        onCancel={() => setShowUnsavedKR(false)}
      />

      {/* ── Delete Confirm ── */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta OKR? Esta ação não pode ser desfeita e todos os Key Results associados serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOKR} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete Key Result Confirm ── */}
      <AlertDialog open={!!deleteKRConfirmId} onOpenChange={() => setDeleteKRConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este Key Result? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteKR} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Cancel Dialog ── */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="h-5 w-5" />
              Cancelar OKR
            </DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. A OKR será movida para o status de cancelada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {okrToCancel && (
              <div className="p-3 rounded-lg bg-muted/50">
                <h4 className="font-medium text-sm">{okrToCancel.titulo}</h4>
                <p className="text-xs text-muted-foreground mt-1">{okrToCancel.descricao}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo do Cancelamento *</Label>
              <Textarea
                id="motivo"
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                placeholder="Descreva o motivo pelo qual esta OKR está sendo cancelada..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setOkrToCancel(null); setMotivoCancelamento(''); }}>
              Voltar
            </Button>
            <Button variant="destructive" onClick={handleCancelarOKR} disabled={!motivoCancelamento.trim()}>
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Historico Dialog ── */}
      <Dialog open={historicoDialogOpen} onOpenChange={(open) => { if (!open) { setHistoricoDialogOpen(false); setHistoricoOkr(null); setAtividades([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Histórico de Atividades
            </DialogTitle>
            {historicoOkr && <DialogDescription>{historicoOkr.titulo}</DialogDescription>}
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {loadingAtividades ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : atividades.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Nenhuma atividade registrada</div>
            ) : (
              <div className="space-y-4">
                {atividades.map((atividade) => {
                  const Icon = getAtividadeIcon(atividade.tipo);
                  return (
                    <div key={atividade.id} className="flex gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="h-8 w-8 rounded-full bg-background flex items-center justify-center border">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={cn('text-xs font-normal', getAtividadeBadgeClass(atividade.tipo))}>
                            {getAtividadeLabel(atividade.tipo)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(atividade.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm font-medium">{atividade.descricao}</p>
                        {atividade.detalhes && <p className="text-xs text-muted-foreground mt-1">{atividade.detalhes}</p>}
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>{atividade.usuario_nome}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHistoricoDialogOpen(false); setHistoricoOkr(null); setAtividades([]); }}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Completion Dialog ── */}
      <Dialog open={completionDialogOpen} onOpenChange={(open) => { if (!open) handleCloseCompletionDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Solicitar Conclusão
            </DialogTitle>
            <DialogDescription>Sua solicitação será enviada para aprovação do administrador.</DialogDescription>
          </DialogHeader>
          {completionKR && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium">{completionKR.descricao}</p>
                <p className="text-sm text-muted-foreground mt-1">( {completionKR.progresso_atual} / {completionKR.progresso_meta} )</p>
                {completionKR.anexo_obrigatorio && (
                  <Badge variant="outline" className="mt-2 gap-1 text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400">
                    <Paperclip className="h-3 w-3" />
                    Anexo obrigatório
                  </Badge>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="completion_progress">Novo Progresso</Label>
                <Input
                  id="completion_progress"
                  type="number"
                  value={completionProgress}
                  onChange={(e) => setCompletionProgress(Number(e.target.value))}
                  onFocus={(e) => { completionProgressJustFocused.current = true; e.target.select(); }}
                  onMouseUp={(e) => { if (completionProgressJustFocused.current) { e.preventDefault(); completionProgressJustFocused.current = false; } }}
                  max={completionKR.progresso_meta}
                  min={completionKR.progresso_atual}
                />
                <p className="text-xs text-muted-foreground">Meta: {completionKR.progresso_meta}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="completion_observacao">Observação (opcional)</Label>
                <Textarea id="completion_observacao" value={completionObservacao} onChange={(e) => setCompletionObservacao(e.target.value)} placeholder="Descreva o que foi feito..." rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="completion_file">Anexo {completionKR.anexo_obrigatorio ? '(obrigatório)' : '(opcional)'}</Label>
                <div className="flex items-center gap-2">
                  <Input id="completion_file" type="file" accept=".pdf,.csv,.png,.jpg,.jpeg,.gif,.webp" onChange={(e) => setCompletionFile(e.target.files?.[0] || null)} className="flex-1" />
                  {completionFile && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setCompletionFile(null)}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {completionFile && <p className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="h-3 w-3" />{completionFile.name}</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseCompletionDialog} disabled={isSubmittingCompletion}>Cancelar</Button>
            <Button onClick={handleRequestCompletion} disabled={isSubmittingCompletion}>
              {isSubmittingCompletion ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {isSubmittingCompletion ? 'Enviando...' : 'Enviar para Aprovação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedCompletion}
        onOpenChange={setShowUnsavedCompletion}
        onConfirm={() => { setShowUnsavedCompletion(false); setCompletionDialogOpen(false); setCompletionKR(null); setCompletionFile(null); setCompletionObservacao(''); }}
        onCancel={() => setShowUnsavedCompletion(false)}
      />

      {/* ── Quick-create Departamento ── */}
      <Dialog open={isNewDepDialogOpen} onOpenChange={(open) => { setIsNewDepDialogOpen(open); if (!open) setNewDepNome(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Departamento</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new_dep_nome">Nome *</Label>
            <Input id="new_dep_nome" value={newDepNome} onChange={(e) => setNewDepNome(e.target.value)} placeholder="Ex: Marketing" onKeyDown={(e) => { if (e.key === 'Enter') handleCreateDepartamento(); }} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsNewDepDialogOpen(false); setNewDepNome(''); }}>Cancelar</Button>
            <Button onClick={handleCreateDepartamento} disabled={!newDepNome.trim() || savingNewDep}>
              {savingNewDep ? 'Criando...' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teste piloto (27-28/09): guia visual do módulo, botão flutuante "?" no canto inferior
          direito. Ver guias-modulos/MODELO.md na raiz do projeto. */}
      <GuiaModuloFlutuante imagemSrc="/guias-modulos/okrs.png" titulo="OKRs" />
    </AppLayout>
  );
}
